import {
	createCollection,
	localOnlyCollectionOptions,
	safeRandomUUID,
} from "@tanstack/react-db"
import { registerSessionCleanup } from "@/shared/lib/auth"
import { pushTapActionOf } from "./placement"
import { preloadNotificationRenderer } from "./renderer-registry"
import type { NotificationInput, NotificationRecord } from "./types"

/** The popup stack: one record per pending popup, newest drawn first. */
export const notificationCollection = createCollection(
	localOnlyCollectionOptions({
		id: "notification-stack",
		getKey: (record: NotificationRecord) => record.stackId,
	}),
)

function resetNotifications(): void {
	dismissNotificationsWhere(() => true)
}

/** Stamps an input with its local key and the timestamp that orders the stack. */
function toRecord(notification: NotificationInput): NotificationRecord {
	return Object.assign({}, notification, {
		stackId: safeRandomUUID(),
		createdAt: Date.now(),
	})
}

/** Puts a notification on top of the stack and warms up its renderer. */
export function showNotification(notification: NotificationInput): string {
	const record = toRecord(notification)
	preloadNotificationRenderer(notification.type)
	notificationCollection.insert(record)
	return record.stackId
}

/**
 * Takes one notification off the stack by its stack id.
 *
 * Idempotent: dismissing an already dismissed notification is routine, and
 * Collection.delete throws on a missing key.
 */
export function dismissNotification(stackId: string): void {
	if (notificationCollection.has(stackId))
		notificationCollection.delete(stackId)
}

/** Matches the records of one server notification. */
export function hasNotificationId(
	notificationId: number,
): (record: NotificationRecord) => boolean {
	return (record) =>
		"notificationId" in record && record.notificationId === notificationId
}

/** Takes out every notification the predicate matches. */
export function dismissNotificationsWhere(
	predicate: (record: NotificationRecord) => boolean,
): void {
	const stackIds: string[] = []
	for (const record of notificationCollection.toArray) {
		if (predicate(record)) stackIds.push(record.stackId)
	}
	if (stackIds.length > 0) notificationCollection.delete(stackIds)
}

/**
 * Swaps a notification on the stack for another one — a friend request for its
 * acceptance.
 *
 * No-op when the record is gone: it was either already replaced or dismissed by
 * the user, and an unconditional insert would produce duplicates.
 */
export function replaceNotification(
	stackId: string,
	notification: NotificationInput,
): void {
	if (!notificationCollection.has(stackId)) return
	preloadNotificationRenderer(notification.type)
	notificationCollection.delete(stackId)
	notificationCollection.insert(toRecord(notification))
}

// The overlay draws the newest record, and the one the user tapped must be it.
function raiseNotification(stackId: string): void {
	notificationCollection.update(stackId, (draft) => {
		draft.createdAt = Date.now()
	})
}

function findByNotificationId(
	notificationId: number,
): NotificationRecord | null {
	for (const record of notificationCollection.toArray) {
		if (record.origin === "server" && record.notificationId === notificationId)
			return record
	}
	return null
}

/**
 * Applies a push tap to its notification's record: raised to the top or
 * dismissed, as its placement decides.
 */
export function applyPushTap(
	notificationId: number,
	onHomeScreen: boolean,
): void {
	const record = findByNotificationId(notificationId)
	if (record === null) return

	const action = pushTapActionOf(record, onHomeScreen)
	if (action === "raise") {
		raiseNotification(record.stackId)
		return
	}
	dismissNotificationsWhere(hasNotificationId(notificationId))
}

/**
 * Ids of everything still on the stack — what the app knows to be unread. The
 * system shade is cleaned against this: a push whose id is gone from here has
 * been answered or was never outstanding in this session.
 */
export function stackedNotificationIds(): number[] {
	const ids: number[] = []
	for (const record of notificationCollection.toArray) {
		if (record.origin === "server") ids.push(record.notificationId)
	}
	return ids
}

registerSessionCleanup(resetNotifications)
